import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useFetchData, usePostData } from "utils/ReactQueryHooks";
import * as request from "utils/ApiClient";
import * as toast from "utils/ApiToast";
import { isData } from "utils/HookResponse";
import { ApiResponseData } from "types/common";
import defaults from "config/forms.json";
import axios, { AxiosProgressEvent } from "axios";

type UploadProgressEvent = {
  loaded: number;
  total: number;
};

export const useFirmwares = () =>
  useFetchData({
    key: "firmwares",
    url: "firmwares",
    data: {
      aId: Cookies.get("aId"),
    },
    options: {
      select: (data: ApiResponseData) => isData(data),
    },
    post: true,
  });

export const useFirmwareNotificationSubmit = () =>
  usePostData({ url: "firmwares/add-notification" });

export const useFirmwareDntSubmit = (
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>
) =>
  useMutation({
    mutationFn: (data: ApiResponseData) => {
      data.aId = Cookies.get("aId");

      return request.post({
        url: "firmwares/upload-file",
        data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        options: {
          onUploadProgress: (progress: UploadProgressEvent) => {
            setUploadProgress(
              Math.round((progress.loaded * 100) / progress.total)
            );
          },
        },
      });
    },
    onSuccess: toast.successError,
    onError: toast.error,
  });

export const useFirmwareDntUpdate = () =>
  usePostData({ url: "firmwares/update-file" });

export const useFirmwareMultipartSubmit = (
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>
) =>
  useMutation({
    mutationFn: async (data: ApiResponseData) => {
      const file = data.file;
      const CHUNK_SIZE = defaults.file.maxSize.chunk * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      const initiateResponse = await request.post({
        url: "firmwares/upload-file/non-dnt/initiate",
        data: {
          ...data,
          fileName: file.name,
          fileSize: file.size,
        },
      });

      if (
        !initiateResponse ||
        !initiateResponse.data ||
        !initiateResponse.data.uploadId ||
        !initiateResponse.data.uploadUrl
      ) {
        throw new Error("Failed to initiate upload");
      }

      const uploadId = initiateResponse.data.uploadId;
      const uploadUrl = initiateResponse.data.uploadUrl;
      const MAX_RETRIES = 3;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        if (chunk.size === 0) continue;

        const chunkData = new FormData();
        chunkData.append("file", chunk, file.name);

        let attempt = 0;
        while (attempt < MAX_RETRIES) {
          try {
            await axios.post(`${uploadUrl}/${uploadId}/${i + 1}`, chunkData, {
              onUploadProgress: (progress: AxiosProgressEvent) => {
                const loaded = progress.loaded || 0;
                const uploadedBytes = start + loaded;
                setUploadProgress(
                  Math.round((uploadedBytes / file.size) * 100)
                );
              },
            });
            break;
          } catch (error) {
            attempt++;
            console.warn(
              `Chunk ${i} failed on attempt ${attempt}:`,
              error.message
            );

            if (attempt === MAX_RETRIES) {
              console.error(`Chunk ${i} failed after ${MAX_RETRIES} attempts.`);
              throw error;
            }

            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
      }

      return await request.post({
        url: "firmwares/upload-file/non-dnt/complete",
        data: {
          ...data,
          fileName: file.name,
          uploadId,
        },
      });
    },
    onSuccess: toast.successError,
    onError: toast.error,
  });
